import type de from './de';

/**
 * UI texts of the web client (Dutch). Common terms follow the legacy dictionaries
 * (lang/internal/nl.json: "Record", "Instellingen", "Afmelden", "Kleurenschema" …).
 */
const nl: typeof de = {
  app: {
    name: 'Creator',
    loading: 'Laden …',
    retry: 'Opnieuw proberen',
    close: 'Sluiten',
    cancel: 'Annuleren',
    ok: 'OK',
    save: 'Opslaan',
    back: 'Terug',
    menu: 'Menu',
    offline: 'Geen verbinding – wijzigingen verschijnen pas na het opnieuw verbinden.',
    updateAvailable: 'Er is een nieuwe versie beschikbaar.',
    updateReload: 'Opnieuw laden',
    skipToContent: 'Naar de inhoud',
  },
  nav: {
    home: 'Start',
    navigation: 'Navigatie',
    database: 'Database',
    filter: 'Tabellen filteren',
    noMatch: 'Geen tabel gevonden',
    favorites: 'Favorieten',
    recent: 'Recent gebruikt',
    other: 'Overige',
    pin: 'Zijbalk vastzetten',
    unpin: 'Zijbalk verbergen',
    toggleSidebar: 'Zijbalk weergeven/verbergen',
    breadcrumb: 'Kruimelpad',
    addFavorite: 'Toevoegen aan favorieten',
    removeFavorite: 'Verwijderen uit favorieten',
  },
  user: {
    printLayouts: 'Afdruklay-outs',
    menu: 'Gebruikersmenu',
    language: 'Taal',
    colorScheme: 'Kleurenschema',
    colorTheme: 'Kleurthema',
    settings: 'Instellingen',
    password: 'Wachtwoord wijzigen',
    twoFactor: 'Tweestapsaanmelding',
    twoFactorOn: 'actief',
    switchUser: 'Gebruiker wisselen',
    switchUserPrompt: 'Als welke gebruiker wilt u werken?',
    switchBack: 'Terug naar {user}',
    logout: 'Afmelden',
    shortcuts: 'Sneltoetsen',
    impersonating: 'Aangemeld als {user} door {admin}.',
  },
  scheme: { auto: 'Automatisch', light: 'Licht', dark: 'Donker' },
  theme: {
    default: 'Standaard',
    blue: 'Blauw',
    green: 'Groen',
    grey: 'Grijs',
    orange: 'Oranje',
    petrol: 'Petrol',
    red: 'Rood',
    sportsman: 'Sportsman',
    yellow: 'Geel',
  },
  lang: { de: 'Deutsch', en: 'English', fr: 'Français', nl: 'Nederlands', auto: 'Browsertaal' },
  login: {
    title: 'Aanmelden',
    username: 'Gebruikersnaam',
    password: 'Wachtwoord',
    submit: 'Aanmelden',
    totpTitle: 'Verificatiecode',
    totpHint: 'Voer de code uit uw authenticator-app of een back-upcode in.',
    totp: 'Code',
    actAs: 'Starten als gebruiker (alleen beheerders)',
    invalid: 'Gebruikersnaam of wachtwoord is onjuist.',
    totpInvalid: 'De code is ongeldig.',
    throttled: 'Te veel pogingen. Wacht {seconds} seconden.',
    throttledShort: 'Te veel pogingen. Probeer het later opnieuw.',
    unavailable: 'Aanmelden is momenteel niet mogelijk.',
    expired: 'Uw sessie is verlopen. Meld u opnieuw aan.',
  },
  home: {
    title: 'Overzicht',
    databases: 'Databases',
    empty: 'Geen tabellen beschikbaar.',
    tables: '{n} tabellen',
  },
  table: {
    rows: 'Records',
    placeholder: 'Voorlopige weergave – de volledige tabelweergave volgt.',
    empty: 'Geen records.',
    new: 'Nieuw',
    refresh: 'Vernieuwen',
    of: 'van',
    live: 'Live-updates actief',
  },
  record: {
    title: 'Record {id}',
    new: 'Nieuw record',
    prev: 'Vorig record',
    next: 'Volgend record',
    readonly: 'alleen lezen',
  },
  account: {
    title: 'Account',
    settings: 'Instellingen',
    password: 'Wachtwoord',
    security: 'Tweestapsaanmelding',
    saved: 'Opgeslagen.',
    localOnly: 'De instellingen worden momenteel alleen op dit apparaat opgeslagen.',
    sectionDisplay: 'Weergave',
    sectionTables: 'Tabellen en records',
    reset: 'Herstellen',
    resetDefault: 'Standaard herstellen',
    showUrl: 'Records openen',
    showUrlOverlay: 'Als overlay boven de tabel',
    showUrlTab: 'In een nieuw tabblad',
    showUrlPage: 'Als aparte pagina',
    margin: 'Celafstand',
    marginNone: 'Geen',
    marginLow: 'Klein',
    marginMedium: 'Gemiddeld',
    marginHigh: 'Groot',
    textSearch: 'Zoekregel in tabellen',
    textSearchNever: 'Nooit weergeven',
    textSearchAlways: 'Altijd weergeven',
    textSearchFilter: 'Alleen weergeven als een filter actief is',
    fixedHeader: 'Tabelkop vastzetten',
    sidebarPinned: 'Zijbalk vastgezet (desktop)',
    pageSize: 'Records per pagina',
    pageSizeDefault: 'Standaard van de tabel',
    help: {
      lang: 'Taal van de gebruikersinterface. "Browsertaal" volgt de instelling van uw browser.',
      colorScheme: 'Licht, donker of automatisch volgens de instelling van het apparaat.',
      theme: 'Accentkleuren van kopregel, zijbalk en knoppen.',
      margin: 'Ruimte binnen de tabelcellen – kleiner toont meer rijen tegelijk.',
      sidebarPinned: 'De zijbalk blijft op grote schermen altijd zichtbaar.',
      showUrlOverlay: 'Opent het record in een groot venster boven de tabel (mobiel schermvullend); de tabel blijft behouden.',
      showUrlTab: 'Opent elk record in een nieuw browsertabblad.',
      showUrlPage: 'Vervangt de tabel door het record; "Terug" gaat naar de tabel.',
      textSearch: 'Regel met zoekvelden onder de tabelkop. "Alleen als een filter actief is" toont deze zodra een filter is ingesteld.',
      fixedHeader: 'De tabelkop blijft zichtbaar tijdens het scrollen.',
      pageSize: 'Hoeveel records een tabel per pagina laadt.',
      shortcuts: 'Klik op + en druk op de gewenste toetsencombinatie (Esc annuleert). Uw eigen toewijzingen worden in uw account opgeslagen.',
    },
    scope_global: 'Overal',
    scope_table: 'Tabel',
    scope_record: 'Record',
    shortcutCustom: 'aangepast',
    shortcutNone: 'geen',
    shortcutAdd: 'Toetsencombinatie toevoegen',
    shortcutRemove: '{key} verwijderen',
    shortcutPress: 'Druk op toetsen …',
    shortcutRecording: 'Opname: druk op de nieuwe toetsencombinatie, Esc annuleert',
    shortcutReserved: 'Deze combinatie is gereserveerd voor de browser.',
    shortcutPlain: 'De spatiebalk alleen kan niet worden toegewezen.',
    shortcutTooMany: 'Te veel toetsencombinaties voor deze actie.',
    shortcutConflictTitle: 'Toetsencombinatie al in gebruik',
    shortcutConflict: '{key} wordt al gebruikt door: {actions}. Daar verwijderen en hier toewijzen?',
    shortcutReassign: 'Opnieuw toewijzen',
    shortcutConflicts: 'Meervoudig gebruikte toetsencombinaties:',
    shortcutResetAll: 'Alles herstellen',
    shortcutResetAllConfirm: 'Alle sneltoetsen terugzetten naar de standaard?',
    shortcutResetDone: 'De sneltoetsen zijn hersteld.',
    oldPassword: 'Huidig wachtwoord',
    newPassword: 'Nieuw wachtwoord',
    repeatPassword: 'Nieuw wachtwoord herhalen',
    changePassword: 'Wachtwoord wijzigen',
    showPasswords: 'Wachtwoorden tonen',
    passwordMismatch: 'De wachtwoorden komen niet overeen.',
    passwordChanged: 'Wachtwoord succesvol gewijzigd. Andere sessies zijn beëindigd.',
    passwordRules: 'Minstens {min} tekens, niet gelijk aan de gebruikersnaam.',
    passwordSessionsHint: 'Na de wijziging worden uw andere sessies (andere apparaten, browsers) afgemeld.',
    oldPasswordWrong: 'Het huidige wachtwoord is onjuist.',
    passwordWeak: 'Het nieuwe wachtwoord voldoet niet aan de regels (minstens {min} tekens, niet gelijk aan de gebruikersnaam).',
    passwordExternal: 'Het wachtwoord van dit account wordt extern beheerd (bijv. LDAP) en kan hier niet worden gewijzigd.',
    pwStrength: 'Wachtwoordsterkte',
    pwScore0: 'Te zwak',
    pwScore1: 'Zwak',
    pwScore2: 'Redelijk',
    pwScore3: 'Goed',
    pwScore4: 'Zeer goed',
    pwHint: {
      tooShort: 'minstens {min} tekens',
      username: 'niet gelijk aan de gebruikersnaam',
      tooLong: 'te lang',
      mix: 'hoofdletters, kleine letters, cijfers en speciale tekens combineren',
      repeat: 'herhalingen en reeksen vermijden',
      common: 'te gebruikelijk',
      longer: 'langer is veiliger',
      sameAsOld: 'gelijk aan het huidige wachtwoord',
    },
    totpEnabled: 'Tweestapsaanmelding is actief.',
    totpDisabled: 'Tweestapsaanmelding is niet actief.',
    totpIntro: 'Bij het aanmelden wordt bovendien een code uit een authenticator-app gevraagd (bijv. FreeOTP, Google Authenticator, Microsoft Authenticator).',
    totpUnavailable: 'Tweestapsaanmelding is op deze server niet geconfigureerd.',
    totpStart: 'Instellen',
    totpStep1: 'Scan de QR-code met uw authenticator-app of voer de sleutel handmatig in.',
    totpStep2: 'Voer ter bevestiging de code in die de app toont.',
    totpQr: 'QR-code voor de authenticator-app',
    totpSecret: 'Sleutel voor de authenticator-app',
    totpAccount: 'Account',
    totpManualHint: 'Tijdgebaseerd (TOTP), 6 cijfers, 30 seconden.',
    totpLink: 'Openen in authenticator-app',
    totpVerify: 'Bevestigen',
    totpCode: 'Code uit de app',
    totpCodeOrBackup: 'Code uit de app of back-upcode',
    totpBackupTitle: 'Back-upcodes',
    totpBackup: 'Bewaar deze codes nu op een veilige plek – ze worden maar één keer getoond. Elke code vervangt één keer de code uit de app, bijv. als u uw telefoon kwijtraakt.',
    totpBackupSaved: 'Ik heb de back-upcodes veilig opgeslagen.',
    copy: 'Kopiëren',
    download: 'Downloaden als tekstbestand',
    secretCopied: 'Sleutel gekopieerd.',
    codesCopied: 'Back-upcodes gekopieerd.',
    copyFailed: 'Kopiëren is niet mogelijk – selecteer de tekst handmatig.',
    done: 'Klaar',
    totpDisable: 'Uitschakelen',
    totpDisableHint: 'Voor het uitschakelen zijn uw wachtwoord en een actuele code (of back-upcode) nodig.',
    totpDisableConfirm: 'Tweestapsaanmelding echt uitschakelen? Uw account is daarna alleen nog door het wachtwoord beschermd.',
    totpDisableWrong: 'Wachtwoord of code is onjuist.',
    totpDisabledDone: 'Tweestapsaanmelding is uitgeschakeld.',
  },
  error: {
    generic: 'Er is een fout opgetreden.',
    network: 'De server is niet bereikbaar.',
    forbidden: 'Geen toegang.',
    notFound: 'Niet gevonden.',
    conflict: 'Conflict met de huidige toestand.',
    stale: 'Het record is intussen door iemand anders gewijzigd.',
    validation: 'Controleer uw invoer.',
    locked: 'Het record wordt momenteel bewerkt.',
    lockedBy: 'Het record wordt momenteel bewerkt door {user}.',
    rateLimited: 'Te veel verzoeken. Wacht even.',
    server: 'Serverfout. Probeer het later opnieuw.',
    pending: 'Deze weergave is nog niet beschikbaar.',
    pendingCompare: 'Het vergelijken van twee records volgt in een van de volgende versies.',
    pendingForm: 'Dit formulier van het oude systeem is nog niet overgenomen.',
    notFoundPage: 'Deze pagina bestaat niet.',
    toHome: 'Naar de startpagina',
  },
  shortcuts: {
    title: 'Sneltoetsen',
    app_search: 'Zoeken / tabellen filteren',
    app_toggleSidebar: 'Zijbalk weergeven/verbergen',
    app_help: 'Sneltoetsen weergeven',
    table_next: 'Volgende rij',
    table_prev: 'Vorige rij',
    table_open: 'Geselecteerd record openen',
    table_new: 'Nieuw record aanmaken',
    table_delete: 'Geselecteerd record verwijderen',
    table_refresh: 'Tabel vernieuwen',
    record_save: 'Opslaan',
    record_saveClose: 'Opslaan en sluiten',
    record_close: 'Sluiten',
    record_next: 'Volgend record',
    record_prev: 'Vorig record',
    record_pageNext: 'Volgende formulierpagina',
    record_pagePrev: 'Vorige formulierpagina',
    browserNote: 'De meeste browsers gebruiken Ctrl+Tab en Ctrl+Shift+Tab om van tabblad te wisselen; de applicatie ontvangt ze niet. Gebruik Ctrl+Alt+→ en Ctrl+Alt+← om van formulierpagina te wisselen.',
  },
  dev: { elements: 'Formulierelementen (ontwikkeling)' },
};
export default nl;
